import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import GoalsMatrix from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix';
import GoalsFilters from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-filters/goals-matrix-filters';
import Heading from 'components/partials/heading/heading';

import {
  HEADERS,
  ROWS,
} from 'containers/admin/clients/client/tabs-components/goals-tab/goals-tab.constants';

import { getClientGoals } from 'api/generated/client-goals/client-goals';

import { useCurrentUser } from 'hooks/api/use-current-user';

import type { Goal } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface GoalsTabProps {
  clientId: string | number;
  selectedYear: string | number;
}

interface CurrentUserShape {
  data?: { oktaUserId?: string };
}

const GoalsTab = ({
  clientId,
  selectedYear,
}: GoalsTabProps): React.JSX.Element => {
  const navigate = useNavigate();
  const [showOnlyDefined, setShowOnlyDefined] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [goals, setGoals] = useState<Goal[]>([]);
  const user = useCurrentUser() as CurrentUserShape;

  useEffect(() => {
    const fetchGoals = async (): Promise<void> => {
      if (!user?.data?.oktaUserId) return;

      setLoading(true);
      try {
        const res = await getClientGoals(
          user.data.oktaUserId,
          Number(clientId),
          {
            'enteringClassYear.equals': Number(selectedYear),
            size: 1000,
            sort: ['createdDate,desc'],
          }
        );

        setGoals((res.content ?? []).map((row) => ({ ...row } as Goal)));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [selectedYear]);

  const handleYearChange = (year: number | string): void => {
    navigate(`/admin/clients/${clientId}/goals/${year}`);
  };

  return (
    <div className="w-full mt-5 flex flex-1 flex-col">
      <Heading
        variant="h2"
        customClass="my-5 block"
        data-testid="goals-tab-title"
      >
        Client enrollment goals for {selectedYear}
      </Heading>
      <GoalsFilters
        showOnlyDefined={showOnlyDefined}
        setShowOnlyDefined={setShowOnlyDefined}
        selectedYear={selectedYear}
        setSelectedYear={handleYearChange}
        customClass="mb-3"
      />
      <GoalsMatrix
        headers={Object.values(HEADERS)}
        rows={Object.values(ROWS)}
        data={goals}
        showOnlyDefined={showOnlyDefined}
        year={selectedYear}
        loading={loading}
      />
    </div>
  );
};

export default GoalsTab;
